import React, { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { downloadFile, ACCEPT_UPLOAD, MAX_UPLOAD_BYTES } from "@/lib/files";
import { ClipboardList, Download, Upload, Trash2, FileText, Plus, Loader2, X } from "lucide-react";

const PHASE_ACCENT = ["#10b981", "#0ea5e9", "#8b5cf6", "#f59e0b", "#ec4899", "#0f172a"];

function UploadForm({ phase, onUploaded, onCancel }) {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file");
    if (file.size > MAX_UPLOAD_BYTES) return toast.error("File exceeds the 10MB limit");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("phase", String(phase));
      fd.append("title", title || file.name);
      fd.append("file", file);
      await api.post("/worksheets/templates", fd);
      toast.success("Worksheet template added");
      onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={`upload-form-${phase}`} className="mt-3 flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-md p-3">
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Worksheet title (optional)" data-testid={`upload-title-${phase}`} className="flex-1 min-w-[180px] border border-slate-300 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
      <input type="file" accept={ACCEPT_UPLOAD} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`upload-file-${phase}`} className="text-sm text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-slate-200 file:text-slate-700 file:text-sm" />
      <button type="submit" disabled={busy} data-testid={`upload-submit-${phase}`} className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1.5 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Add
      </button>
      <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700 p-2"><X className="h-4 w-4" /></button>
    </form>
  );
}

function PhaseSection({ phase, refresh }) {
  const [showUpload, setShowUpload] = useState(false);
  const accent = PHASE_ACCENT[phase.phase - 1] || "#10b981";

  const remove = async (id) => {
    try { await api.delete(`/worksheets/templates/${id}`); toast.success("Template removed"); refresh(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <section data-testid={`worksheet-phase-${phase.phase}`} className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md flex items-center justify-center text-white font-display font-bold text-sm" style={{ background: accent }}>{phase.phase}</div>
          <div>
            <h3 className="font-display font-semibold text-slate-900">{phase.name}</h3>
            <p className="text-xs text-slate-400">{phase.builtin.length + phase.custom.length} worksheet{phase.builtin.length + phase.custom.length === 1 ? "" : "s"}</p>
          </div>
        </div>
        <button data-testid={`add-worksheet-${phase.phase}`} onClick={() => setShowUpload((v) => !v)} className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800">
          <Plus className="h-4 w-4" /> Add worksheet
        </button>
      </div>

      <div className="p-5 space-y-2">
        {phase.builtin.map((w) => (
          <div key={w.id} data-testid={`worksheet-row-${w.id}`} className="flex items-center justify-between gap-3 rounded-md border border-slate-100 hover:border-slate-200 px-3 py-2.5 transition-colors">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{w.title} <span className="ml-1 text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">Starter</span></p>
                <p className="text-xs text-slate-400 truncate">{w.description}</p>
              </div>
            </div>
            <button data-testid={`download-builtin-${w.id}`} onClick={() => downloadFile(`/worksheets/builtin/${w.id}/pdf`, `${w.title}.pdf`)} className="text-slate-500 hover:text-emerald-600 flex items-center gap-1.5 text-sm flex-shrink-0">
              <Download className="h-4 w-4" /> PDF
            </button>
          </div>
        ))}

        {phase.custom.map((c) => (
          <div key={c.id} data-testid={`worksheet-row-${c.id}`} className="flex items-center justify-between gap-3 rounded-md border border-slate-100 hover:border-slate-200 px-3 py-2.5 transition-colors">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="h-4 w-4 text-sky-600 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{c.title}</p>
                <p className="text-xs text-slate-400 truncate">{c.original_filename}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button data-testid={`download-custom-${c.id}`} onClick={() => downloadFile(`/worksheets/templates/${c.id}/download`, c.original_filename)} className="text-slate-500 hover:text-emerald-600 flex items-center gap-1.5 text-sm">
                <Download className="h-4 w-4" /> File
              </button>
              <button data-testid={`delete-custom-${c.id}`} onClick={() => remove(c.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}

        {phase.builtin.length === 0 && phase.custom.length === 0 && (
          <p className="text-sm text-slate-400">No worksheets for this phase yet.</p>
        )}

        {showUpload && <UploadForm phase={phase.phase} onUploaded={() => { setShowUpload(false); refresh(); }} onCancel={() => setShowUpload(false)} />}
      </div>
    </section>
  );
}

export default function WorksheetsPage() {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/worksheets").then((r) => setPhases(r.data.phases)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Layout>
      <header className="h-16 flex items-center gap-2 px-6 border-b border-slate-200 bg-white/70 backdrop-blur-xl z-20 sticky top-0">
        <ClipboardList className="h-5 w-5 text-emerald-600" />
        <h1 className="font-display text-xl font-bold text-slate-900">Worksheets</h1>
      </header>

      <div className="flex-1 overflow-y-auto p-6">
        <p className="text-slate-600 mb-6 max-w-2xl">
          A repository of working worksheets for your project team, organized by the six phases of the framework.
          Download a starter template, or add your own. Completed worksheets can be uploaded to a project's
          <span className="font-medium text-slate-700"> Supporting Materials</span> within each phase.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading worksheets…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="worksheets-grid">
            {phases.map((p) => <PhaseSection key={p.phase} phase={p} refresh={load} />)}
          </div>
        )}
      </div>
    </Layout>
  );
}
